/* eslint-disable react-hooks/exhaustive-deps */
/**
 * YatirimOneriMotoru.jsx
 * ───────────────────────
 * Yatırım Önerisi Motoru — Tüm modülleri entegre eden üst katman
 */

import { useState, useEffect, useRef } from "react";
import { supabase } from "../supabaseClient";
import axios from "axios";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { getLookupParaBirimleri } from "./apiCache";
import { API_URL } from "../config";

// ─── Renk Paleti ─────────────────────────────────────────────────────────────
const C = {
    bg:      "#0f172a",
    panel:   "#1e293b",
    border:  "#334155",
    text:    "#e2e8f0",
    muted:   "#94a3b8",
    accent:  "#3b82f6",
    bullish: "#16a34a",
    bearish: "#dc2626",
    warning: "#f59e0b",
    gold:    "#eab308",
    purple:  "#7c3aed",
    teal:    "#0d9488",
};

// ─── Yardımcılar ──────────────────────────────────────────────────────────────
const fmt = (v, digits = 2) =>
    v == null ? "—" : Number(v).toLocaleString("tr-TR", { minimumFractionDigits: digits, maximumFractionDigits: digits });

const fmtPct = (v) => v == null ? "—" : `%${Number(v).toFixed(2)}`;

const getToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token;
};

function formatSure(sn) {
    if (sn == null || sn < 0) return "—";
    const h = Math.floor(sn / 3600);
    const m = Math.floor((sn % 3600) / 60);
    const s = Math.floor(sn % 60);
    if (h > 0) return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
    return `${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
}

// ─── Renk yardımcıları ───────────────────────────────────────────────────────
const tierRenk = (tier = "") => {
    if (tier.includes("Çok Yüksek")) return { bg: C.bullish + "22", c: C.bullish,  border: C.bullish + "55" };
    if (tier.includes("Yüksek"))     return { bg: C.accent  + "22", c: C.accent,   border: C.accent  + "55" };
    if (tier.includes("Orta"))       return { bg: C.gold    + "22", c: C.gold,     border: C.gold    + "55" };
    return                                   { bg: C.border  + "55", c: C.muted,   border: C.border };
};

const yonRenk = (yon) => yon === "AL"
    ? { bg: C.bullish + "22", c: C.bullish }
    : { bg: C.bearish + "22", c: C.bearish };

const skorRenk = (s) =>
    s >= 85 ? C.bullish : s >= 75 ? C.accent : s >= 65 ? C.warning : C.bearish;

const rrRenk = (rr) =>
    rr >= 3 ? C.bullish : rr >= 2 ? C.accent : rr >= 1.5 ? C.warning : C.bearish;

const sentimentRenk = (etiket) =>
    etiket === "Pozitif" ? C.bullish : etiket === "Negatif" ? C.bearish : C.muted;

// ─── Bilgi Kartı İçerikleri ───────────────────────────────────────────────────
const BILGI_ICERIK = {
    skor: {
        baslik: "Skor",
        aciklama: "Karar Motoru, ML Güven ve Piyasa Rejim puanlarının ağırlıklı birleşimiyle hesaplanan toplam skor (0-100). Yüksek skor, sinyalin birden fazla katmanda güçlü doğrulama aldığı anlamına gelir.",
    },
    guven: {
        baslik: "Güven",
        aciklama: "Toplam Skor'a göre belirlenen güven kademesi: Çok Yüksek, Yüksek, Orta veya Düşük. Bildirim tercihleri de bu kademeye göre ayarlanır — örn. yalnızca 'Çok Yüksek' önerilerde e-posta almayı seçebilirsin.",
    },
    giris: {
        baslik: "Giriş",
        aciklama: "Önerinin üretildiği andaki referans fiyat — pozisyona bu seviyeden veya yakınından girilmesi önerilir. Fiyat bu seviyeden önemli ölçüde uzaklaşırsa öneri güncelliğini yitirebilir.",
    },
    hedef: {
        baslik: "Hedef",
        aciklama: "Kâr alma (take-profit) hedef fiyatı. Karar Motoru'nun teknik yapı (FVG, OB, likidite hedefleri vb.) analizine göre belirlenir. Fiyat bu seviyeye ulaştığında pozisyonun kapatılması önerilir.",
    },
    stop: {
        baslik: "Stop",
        aciklama: "Zarar durdurma (stop-loss) seviyesi. Fiyat bu seviyeyi kırarsa öneri otomatik olarak backend tarafından iptal edilir. Sermaye korumasının temel sınırıdır — bu seviye olmadan pozisyona girilmemelidir.",
    },
    rr: {
        baslik: "R:R (Risk:Getiri)",
        aciklama: "Beklenen kazancın, göze alınan riske oranı (Hedef−Giriş) / (Giriş−Stop). 2:1 ve üzeri genelde sağlıklı kabul edilir — yani potansiyel kazanç, göze alınan riskin en az 2 katı.",
    },
    kazanc_pct: {
        baslik: "Kazanç %",
        aciklama: "Fiyat hedefe ulaşırsa elde edilecek yüzdesel getiri. Giriş fiyatına göre hesaplanır. Bu, pozisyon başarılı olduğunda beklenen üst sınır kazançtır.",
    },
    kayip_pct: {
        baslik: "Kayıp %",
        aciklama: "Fiyat stop seviyesini kırarsa oluşacak yüzdesel zarar. Giriş fiyatına göre hesaplanır. Pozisyon büyüklüğü belirlenirken bu oran, portföy risk limitleriyle birlikte değerlendirilir.",
    },
    pozisyon: {
        baslik: "Pozisyon",
        aciklama: "Risk Filtresi'nin önerdiği, portföyün ne kadarının bu işleme ayrılması gerektiği (%). Risk Skoru yüksek sembollerde bu oran otomatik olarak küçültülür; toplam portföy riskini sınırlamayı amaçlar.",
    },
    karar: {
        baslik: "Karar",
        aciklama: "Karar Motoru'nun ürettiği nihai karar metni (emoji + etiket, örn. '🟢 GÜÇLÜ AL'). Teknik yapı, konfluans ve eşik kontrollerinin özetlenmiş sonucudur; toplam skor bu karara göre ağırlıklandırılır.",
    },
};

function BilgiIkonu({ karti }) {
    const [acik, setAcik] = useState(false);
    const bilgi = BILGI_ICERIK[karti];
    if (!bilgi) return null;
    return (
        <span
            onMouseEnter={e => { e.stopPropagation(); setAcik(true); }}
            onMouseLeave={e => { e.stopPropagation(); setAcik(false); }}
            onClick={e => e.stopPropagation()}
            style={{
                position: "relative", marginLeft: 4, fontSize: 11, color: C.muted, cursor: "help",
                borderRadius: "50%", border: `1px solid ${C.border}`,
                padding: "0 4px", lineHeight: "16px", display: "inline-block",
                userSelect: "none",
            }}>
            ℹ
            {acik && (
                <div style={{
                    position: "absolute", top: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)",
                    background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8,
                    padding: "10px 12px", width: 260, boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                    zIndex: 1000, textAlign: "left", whiteSpace: "normal", fontWeight: 400,
                }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 4 }}>{bilgi.baslik}</div>
                    <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.6 }}>{bilgi.aciklama}</div>
                </div>
            )}
        </span>
    );
}

// ─── Bileşenler ───────────────────────────────────────────────────────────────

function GuvenBadge({ tier }) {
    const { bg, c, border } = tierRenk(tier);
    return (
        <span style={{
            padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
            background: bg, color: c, border: `1px solid ${border}`, whiteSpace: "nowrap"
        }}>
            {tier || "—"}
        </span>
    );
}

// ─── Tarama İlerleme Paneli ───────────────────────────────────────────────────
function YomIlerlemePaneli({ ilerleme, mesaj, bulunanSayisi, tamamlanan, toplamSayisi, gecenSure, tahminiSure, mod }) {
    return (
        <div style={{
            marginBottom: 16, background: C.panel,
            border: `2px solid ${C.purple}44`, borderRadius: 12, padding: 20,
            boxShadow: `0 0 20px ${C.purple}18`,
        }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <span style={{ fontSize: 20 }}>🎯</span>
                <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>
                    Yatırım Önerisi Motoru çalışıyor — {mod}
                </span>
                <span style={{ marginLeft: "auto", fontSize: 22, fontWeight: 800, color: C.purple }}>
                    %{ilerleme.toFixed(0)}
                </span>
            </div>

            <div style={{ height: 10, background: C.bg, borderRadius: 6, overflow: "hidden", marginBottom: 12, border: `1px solid ${C.border}` }}>
                <div style={{
                    height: "100%", width: `${ilerleme}%`,
                    background: `linear-gradient(90deg, ${C.purple}, ${C.accent})`,
                    borderRadius: 6, transition: "width 0.5s ease",
                }} />
            </div>

            <div style={{ fontSize: 13, color: C.text, marginBottom: 14, minHeight: 18 }}>
                {mesaj || "Analiz ediliyor..."}
            </div>

            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>TAMAMLANAN</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.accent }}>
                        {tamamlanan.toLocaleString("tr-TR")} / {toplamSayisi.toLocaleString("tr-TR")}
                    </div>
                </div>
                <div>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>BULUNAN</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.accent }}>{bulunanSayisi}</div>
                </div>
                <div>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>GEÇEN SÜRE</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.teal }}>{formatSure(gecenSure)}</div>
                </div>
                <div>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>TAHMİNİ KALAN</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.gold }}>
                        {tahminiSure != null ? formatSure(tahminiSure) : "Hesaplanıyor..."}
                    </div>
                </div>
            </div>
        </div>
    );
}

function DetayKart({ oneri, onIptal }) {
    const sentiment = oneri.sentiment;
    return (
        <tr>
            <td colSpan={13} style={{ background: C.bg, padding: "14px 20px", borderBottom: `2px solid ${C.border}` }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>

                    {/* Modül Skorları */}
                    <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 16px" }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 10 }}>
                            📊 Modül Skorları
                        </div>
                        {[
                            { label: "Karar Motoru", val: oneri.karar_motoru_skoru, bold: false },
                            { label: "ML Güven",     val: oneri.ml_guven_skoru,     bold: false },
                            { label: "Rejim Puanı",  val: oneri.rejim_puan,         bold: false },
                            { label: "Toplam Skor",  val: oneri.toplam_skor,        bold: true },
                        ].map(({ label, val, bold }) => (
                            <div key={label} style={{ marginBottom: 7 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 3 }}>
                                    <span style={{ color: C.muted, fontWeight: bold ? 700 : 400 }}>{label}</span>
                                    <span style={{ color: skorRenk(val || 0), fontWeight: 700 }}>
                                        {val != null ? `${Number(val).toFixed(0)}/100` : "—"}
                                    </span>
                                </div>
                                <div style={{ height: 4, background: C.bg, borderRadius: 99, overflow: "hidden", border: `1px solid ${C.border}` }}>
                                    <div style={{
                                        height: "100%", borderRadius: 99,
                                        width: `${Math.min(100, val || 0)}%`,
                                        background: bold ? C.purple : (val >= 80 ? C.bullish : val >= 65 ? C.accent : C.warning),
                                        transition: "width 0.4s"
                                    }} />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* İşlem Detayları */}
                    <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 16px" }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 10 }}>
                            💼 İşlem Planı
                        </div>
                        {[
                            { label: "Giriş Fiyatı",      val: fmt(oneri.giris_fiyati,  4) },
                            { label: "Hedef Fiyat",        val: fmt(oneri.hedef_fiyat,   4) },
                            { label: "Stop Loss",          val: fmt(oneri.stop_loss,      4) },
                            { label: "R:R Oranı",          val: oneri.rr != null ? `${oneri.rr}:1` : "—",
                              renk: rrRenk(oneri.rr || 0) },
                            { label: "Beklenen Kazanç",    val: fmtPct(oneri.kazanc_pct), renk: C.bullish },
                            { label: "Max Kayıp",          val: fmtPct(oneri.kayip_pct),  renk: C.bearish },
                            { label: "Pozisyon",           val: `%${oneri.pozisyon_yuzdesi || "—"} portföy` },
                            { label: "Max Pozisyon",       val: fmt(oneri.max_pozisyon, 0) },
                        ].map(({ label, val, renk }) => (
                            <div key={label} style={{ display: "flex", justifyContent: "space-between",
                                fontSize: 12, padding: "4px 0", borderBottom: `1px solid ${C.border}` }}>
                                <span style={{ color: C.muted }}>{label}</span>
                                <span style={{ fontWeight: 700, color: renk || C.text }}>{val}</span>
                            </div>
                        ))}
                    </div>

                    {/* Sentiment & Açıklama & İptal */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 16px" }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 }}>
                                🌍 Piyasa Rejimi
                            </div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: C.accent }}>
                                {oneri.rejim || "—"}
                            </div>
                            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                                {oneri.vade} · {oneri.para_birimi}
                            </div>
                        </div>

                        {sentiment && (
                            <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 16px" }}>
                                <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 }}>
                                    📰 Haber Duygusu
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: sentimentRenk(sentiment.etiket) }}>
                                    {sentiment.etiket}
                                </div>
                                <div style={{ fontSize: 11, color: C.muted }}>
                                    Skor: {Number(sentiment.skor || 0).toFixed(2)}
                                </div>
                            </div>
                        )}

                        {oneri.aciklama && (
                            <div style={{ background: C.accent + "18", borderRadius: 10, padding: "10px 14px",
                                border: `1px solid ${C.accent}44` }}>
                                <div style={{ fontSize: 11, color: C.accent, fontWeight: 600 }}>
                                    💡 {oneri.aciklama}
                                </div>
                            </div>
                        )}

                        <button onClick={() => onIptal(oneri.symbol, oneri.mod)}
                            style={{ padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.bearish}44`,
                                background: "transparent", color: C.bearish, cursor: "pointer",
                                fontSize: 12, fontWeight: 600, marginTop: "auto" }}>
                            ❌ Öneriyi İptal Et
                        </button>
                    </div>
					{/* Likidite */}
					{oneri.likidite_etiket && (
						<div style={{ background: "white", borderRadius: 10, padding: "12px 16px",
							boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
							<div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6 }}>
								💧 Likidite
							</div>
							<div style={{ fontSize: 13, fontWeight: 700,
								color: oneri.likidite_skoru >= 60 ? "#16a34a" :
									   oneri.likidite_skoru >= 40 ? "#d97706" : "#dc2626" }}>
								{oneri.likidite_etiket}
							</div>
							{oneri.ort_hacim_fmt && (
								<div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
									Ort. hacim: {oneri.ort_hacim_fmt}
								</div>
							)}
							{oneri.slip_pct != null && (
								<div style={{ fontSize: 11, color: oneri.slip_pct > 1 ? "#dc2626" : "#9ca3af", marginTop: 1 }}>
									Tahmini slippage: %{Number(oneri.slip_pct).toFixed(3)}
								</div>
							)}
							{oneri.likidite_kucultuldu && (
								<div style={{ marginTop: 4, fontSize: 11, fontWeight: 600,
									color: "#d97706", background: "#fffbeb",
									border: "1px solid #fde68a", borderRadius: 6, padding: "2px 6px" }}>
									⚠️ Pozisyon likidite nedeniyle küçültüldü
								</div>
							)}
							{oneri.likidite_uyari && (
								<div style={{ marginTop: 4, fontSize: 10, color: "#9ca3af" }}>
									{oneri.likidite_uyari}
								</div>
							)}
						</div>
					)}
                </div>
            </td>
        </tr>
    );
}

// ─── İndir Menüsü (Excel / CSV / PDF / SVG) ──────────────────────────────────
function IndirMenusu({ veri, setIslemMenuAcik }) {
    const [acik, setAcik] = useState(false);
    const dosyaAdi = `yatirim_onerisi_${new Date().toLocaleDateString("tr-TR").replace(/\./g, "-")}`;

    const indir = async (fmtTip) => {
        setAcik(false); setIslemMenuAcik(false);
        if (!veri?.length) return;
        if (fmtTip === "xlsx") {
            const ws = XLSX.utils.json_to_sheet(veri);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Yatırım Önerileri");
            XLSX.writeFile(wb, `${dosyaAdi}.xlsx`);
        } else if (fmtTip === "csv") {
            const h = Object.keys(veri[0] || {});
            const rows = veri.map(r => h.map(k => { const v = String(r[k] ?? ""); return v.includes(",") || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v; }).join(","));
            const blob = new Blob(["\uFEFF" + [h.join(","), ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = `${dosyaAdi}.csv`; a.click(); URL.revokeObjectURL(url);
        } else if (fmtTip === "pdf") {
            try {
                const { default: jsPDF } = await import("jspdf");
                const { default: autoTable } = await import("jspdf-autotable");
                const h = Object.keys(veri[0] || {});
                const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
                doc.setFontSize(14); doc.text("Yatırım Önerisi Motoru Sonuçları", 20, 30);
                doc.setFontSize(9); doc.setTextColor(100);
                doc.text(`${veri.length} kayıt · ${new Date().toLocaleDateString("tr-TR")}`, 20, 48);
                autoTable(doc, {
                    head: [h], body: veri.map(r => h.map(k => String(r[k] ?? ""))), startY: 58,
                    styles: { fontSize: 7, cellPadding: 3 },
                    headStyles: { fillColor: [124, 58, 237], textColor: 255, fontStyle: "bold" },
                    alternateRowStyles: { fillColor: [245, 247, 250] },
                });
                doc.save(`${dosyaAdi}.pdf`);
            } catch { toast.error("PDF oluşturulamadı"); }
        } else if (fmtTip === "svg") {
            const h = Object.keys(veri[0] || {});
            const cw = Math.max(60, Math.floor(740 / h.length)), rh = 18, hh = 24, tw = cw * h.length;
            const pw = Math.max(tw + 40, 800), ph = 80 + hh + veri.length * rh;
            const esc = s => String(s).slice(0, 18).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
            const rows = veri.map((r, i) => `<g transform="translate(20,${60 + hh + i * rh})"><rect width="${tw}" height="${rh}" fill="${i % 2 === 0 ? "#1e293b" : "#0f172a"}" stroke="#334155" stroke-width="0.5"/>${h.map((k, j) => `<text x="${j * cw + 4}" y="13" font-size="9" fill="#e2e8f0" font-family="Arial">${esc(r[k])}</text>`).join("")}</g>`).join("");
            const heads = h.map((k, j) => `<text x="${20 + j * cw + 4}" y="48" font-size="9" font-weight="bold" fill="white" font-family="Arial">${esc(k)}</text>`).join("");
            const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${pw}" height="${ph}"><rect width="${pw}" height="${ph}" fill="#0f172a"/><text x="20" y="22" font-size="14" font-weight="bold" fill="#e2e8f0" font-family="Arial">Yatırım Önerisi Motoru Sonuçları</text><text x="20" y="38" font-size="10" fill="#94a3b8" font-family="Arial">${veri.length} kayıt · ${new Date().toLocaleDateString("tr-TR")}</text><rect x="20" y="52" width="${tw}" height="${hh}" fill="#7c3aed"/>${heads}${rows}</svg>`;
            const blob = new Blob([svg], { type: "image/svg+xml" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = `${dosyaAdi}.svg`; a.click(); URL.revokeObjectURL(url);
        }
    };

    return (
        <div style={{ position: "relative" }} onMouseEnter={() => setAcik(true)} onMouseLeave={() => setAcik(false)}>
            <button style={{ display: "flex", width: "100%", textAlign: "left", justifyContent: "space-between",
                padding: "10px 16px", border: "none", background: "none", cursor: "pointer",
                color: C.text, fontSize: 13, borderBottom: `1px solid ${C.border}` }}>
                📥 İndir <span style={{ color: C.muted }}>▶</span>
            </button>
            {acik && (
                <div style={{ position: "absolute", left: "100%", top: 0, background: C.panel,
                    border: `1px solid ${C.border}`, borderRadius: 8,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.4)", minWidth: 160, overflow: "hidden", zIndex: 101 }}>
                    {[{ l: "Excel (.xlsx)", f: "xlsx" }, { l: "CSV (.csv)", f: "csv" }, { l: "PDF (.pdf)", f: "pdf" }, { l: "SVG (.svg)", f: "svg" }].map(s => (
                        <button key={s.f} onClick={() => indir(s.f)}
                            style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 14px",
                                border: "none", background: "none", cursor: "pointer", color: C.text, fontSize: 13,
                                borderBottom: `1px solid ${C.border}` }}>{s.l}</button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── Alarm Popup ──────────────────────────────────────────────────────────────
function AlarmPopup({ hisseler, onKapat, onKaydet }) {
    const [hedefFiyatlar, setHedefFiyatlar] = useState({});
    const [bildirim, setBildirim] = useState(true);
    const [mail, setMail] = useState(false);
    const [kaydediliyor, setKaydediliyor] = useState(false);

    const kaydet = async () => {
        const eksik = hisseler.find(h => !hedefFiyatlar[h.symbol] || isNaN(parseFloat(hedefFiyatlar[h.symbol])));
        if (eksik) { toast.error(`${eksik.symbol} için hedef fiyat girin`); return; }
        setKaydediliyor(true);
        await onKaydet(hisseler, hedefFiyatlar, bildirim, mail);
        setKaydediliyor(false);
    };

    return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
            <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14,
                padding: "1.75rem", width: 460, maxWidth: "95vw", maxHeight: "85vh", overflowY: "auto",
                boxShadow: "0 12px 48px rgba(0,0,0,0.5)", color: C.text }}>
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>⏰ Alarm Ekle</div>
                <p style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
                    <strong>{hisseler.length}</strong> sembol için hedef fiyat girin.
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                    {hisseler.map(h => (
                        <div key={h.symbol} style={{ display: "flex", alignItems: "center", gap: 10,
                            background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px" }}>
                            <span style={{ fontWeight: 700, color: C.accent, minWidth: 80 }}>{h.symbol}</span>
                            <span style={{ fontSize: 12, color: C.muted, minWidth: 90 }}>
                                Giriş: {fmt(h.giris_fiyati, 2)}
                            </span>
                            <input type="number" placeholder="Hedef fiyat" value={hedefFiyatlar[h.symbol] || ""}
                                onChange={e => setHedefFiyatlar(p => ({ ...p, [h.symbol]: e.target.value }))}
                                style={{ flex: 1, padding: "6px 10px", borderRadius: 6, border: `1px solid ${C.border}`,
                                    background: C.panel, color: C.text, fontSize: 13, outline: "none" }} />
                        </div>
                    ))}
                </div>
                <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                        <input type="checkbox" checked={bildirim} onChange={e => setBildirim(e.target.checked)} /> Uygulama içi bildirim
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
                        <input type="checkbox" checked={mail} onChange={e => setMail(e.target.checked)} /> E-posta bildirimi
                    </label>
                </div>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <button onClick={onKapat}
                        style={{ padding: "8px 18px", borderRadius: 8, border: `1px solid ${C.border}`,
                            background: "transparent", cursor: "pointer", fontSize: 13, color: C.muted }}>İptal</button>
                    <button onClick={kaydet} disabled={kaydediliyor}
                        style={{ padding: "8px 20px", borderRadius: 8, border: "none",
                            background: kaydediliyor ? C.border : "linear-gradient(135deg,#1d4ed8,#3b82f6)",
                            color: "white", cursor: kaydediliyor ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600 }}>
                        {kaydediliyor ? "Kaydediliyor..." : "Alarmları Kaydet"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Ana Bileşen ──────────────────────────────────────────────────────────────

export default function YatirimOneriMotoru() {

    // Tab & Parametreler
    const [piyasa,      setPiyasa]      = useState("BIST");
    const [vade,        setVade]        = useState("Orta Vade (11-60 gün)");
    const [paraBirimi,  setParaBirimi]  = useState("TL");
    const [paraBirimiSecenekleri, setParaBirimiSecenekleri] = useState(["TL", "USD"]);

    // Tarama durumu
    const [calisiyor,   setCalisiyor]   = useState(false);
    const [ilerleme,    setIlerleme]    = useState(0);
    const [durumMesaj,  setDurumMesaj]  = useState(null);
    const [bulunanSayisi, setBulunanSayisi] = useState(0);
    const [tamamlanan,  setTamamlanan]  = useState(0);
    const [toplamSayisi,setToplamSayisi]= useState(0);
    const [gecenSure,   setGecenSure]   = useState(0);
    const [tahminiSure, setTahminiSure] = useState(null);

    // Veriler
    const [aktifOneriler, setAktifOneriler] = useState([]);
    const [taramaSonucu,  setTaramaSonucu]  = useState(null);

    // UI durumu
    const [detayAcik,   setDetayAcik]   = useState(null); // symbol
    const [tab,         setTab]         = useState("aktif"); // "aktif" | "tarama"
    const [filtreYon,   setFiltreYon]   = useState("Tümü");
    const [filtreTier,  setFiltreTier]  = useState("Tümü");
    const [filtreVade,  setFiltreVade]  = useState("Tümü");
    const [siralama,    setSiralama]    = useState({ kol: "toplam_skor", yon: "desc" });

    // Seçim & İşlemler menüsü
    const [secilenler,    setSecilenler]    = useState(new Set());
    const [islemMenuAcik, setIslemMenuAcik] = useState(false);
    const [alarmPopup,    setAlarmPopup]    = useState(false);
    const menuRef = useRef(null);

    // Bildirim modal
    const [bildirimModal, setBildirimModal] = useState(false);
    const [bildirimTercih, setBildirimTercih] = useState({
        eposta_cok_yuksek: true, eposta_yuksek: true,
        eposta_orta: false, eposta_dusuk: false
    });
    const [bildirimKaydediliyor, setBildirimKaydediliyor] = useState(false);

    const intervalRef   = useRef(null);
    const gecenSureRef  = useRef(null);

    // ── İlk yükleme: aktif öneriler + bildirim tercihi ─────────────────────
    useEffect(() => {
        aktifOnerileriYukle(piyasa);
        bildirimTercihiYukle();
        return () => {
            clearInterval(intervalRef.current);
            clearInterval(gecenSureRef.current);
        };
    }, []);

    useEffect(() => {
        aktifOnerileriYukle(piyasa);
        setTabDurumunuSifirla();
    }, [piyasa]);

    // Para Birimi seçeneklerini DB'den çek, BIST100 seçeneklerini filtrele
    useEffect(() => {
        getLookupParaBirimleri(piyasa, "yatirim_onerisi")
            .then(pb => {
                if (pb?.length) {
                    const filtreli = pb.filter(v => v !== "BIST100 (TL)" && v !== "BIST100 (USD)");
                    setParaBirimiSecenekleri(filtreli);
                    if (!filtreli.includes(paraBirimi)) setParaBirimi(filtreli[0]);
                }
            })
            .catch(() => {
                // Fallback: DB yoksa sabit liste
                const fallback = piyasa === "BIST" ? ["TL", "USD"] : ["USD", "USD (Futures)"];
                setParaBirimiSecenekleri(fallback);
                if (!fallback.includes(paraBirimi)) setParaBirimi(fallback[0]);
            });
    }, [piyasa]);

    useEffect(() => {
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setIslemMenuAcik(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const setTabDurumunuSifirla = () => {
        setTaramaSonucu(null);
        setBulunanSayisi(0);
        setFiltreYon("Tümü");
        setFiltreTier("Tümü");
        setFiltreVade("Tümü");
        setDetayAcik(null);
        setSecilenler(new Set());
        setParaBirimi(piyasa === "BIST" ? "TL" : "USD");
    };

    const aktifOnerileriYukle = async (mod) => {
        try {
            const token = await getToken();
            const res = await axios.get(`${API_URL}/scan/yom/aktif?mod=${mod}`,
                { headers: { Authorization: `Bearer ${token}` } });
            setAktifOneriler(res.data.oneriler || []);
        } catch (e) {
            console.error("Aktif öneriler yüklenemedi:", e);
        }
    };

    const bildirimTercihiYukle = async () => {
        try {
            const token = await getToken();
            const res = await axios.get(`${API_URL}/scan/yom/bildirim-tercih`,
                { headers: { Authorization: `Bearer ${token}` } });
            setBildirimTercih(res.data);
        } catch { }
    };

    // ── Durum sorgula ────────────────────────────────────────────────────────
    const durumSorgula = async () => {
        try {
            const token = await getToken();
            const res = await axios.get(`${API_URL}/scan/yom/durum`,
                { headers: { Authorization: `Bearer ${token}` } });
            setIlerleme(res.data.ilerleme || 0);
            setTahminiSure(res.data.tahmini_sure);

            const mesaj = res.data.mesaj || "";
            if (mesaj.includes("|")) {
                const p = mesaj.split("|");
                const b = parseInt(p[0], 10);
                if (!isNaN(b)) setBulunanSayisi(b);
                if (p[1] && p[1].includes("/")) {
                    const [t, top] = p[1].split("/");
                    if (!isNaN(parseInt(t, 10)))   setTamamlanan(parseInt(t, 10));
                    if (!isNaN(parseInt(top, 10))) setToplamSayisi(parseInt(top, 10));
                }
                setDurumMesaj(p[2] ? `Taranan: ${p[2]}` : p[1] || mesaj);
            } else {
                setDurumMesaj(mesaj);
            }

            if (!res.data.calisiyor) {
                clearInterval(intervalRef.current);
                clearInterval(gecenSureRef.current);
                setCalisiyor(false);
                if (res.data.sonuc) {
                    setTaramaSonucu(res.data.sonuc);
                    setBulunanSayisi(res.data.sonuc.toplam || 0);
                    aktifOnerileriYukle(piyasa);
                    setTab("tarama");
                    toast.success(`✅ Tamamlandı! ${res.data.sonuc.toplam || 0} öneri üretildi`);
                } else if (res.data.hata) {
                    toast.error("Hata: " + res.data.hata);
                }
            }
        } catch { }
    };

    // ── Tarama Başlat ────────────────────────────────────────────────────────
    const taramaBaslat = async () => {
        setTaramaSonucu(null);
        setBulunanSayisi(0);
        setTamamlanan(0);
        setToplamSayisi(0);
        setCalisiyor(true);
        setIlerleme(0);
        setDurumMesaj(null);
        setGecenSure(0);
        const simdi = Date.now();
        clearInterval(gecenSureRef.current);
        gecenSureRef.current = setInterval(
            () => setGecenSure(Math.floor((Date.now() - simdi) / 1000)), 1000
        );
        try {
            const token = await getToken();
            await axios.post(`${API_URL}/scan/yom/baslat`, {
                mod:         piyasa,
                vade,
                para_birimi: paraBirimi,
            }, { headers: { Authorization: `Bearer ${token}` } });
            toast.success("Yatırım Önerisi Motoru başlatıldı");
            intervalRef.current = setInterval(durumSorgula, 3000);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Hata oluştu");
            setCalisiyor(false);
            clearInterval(gecenSureRef.current);
        }
    };

    // ── Öneri İptal (tekil) ──────────────────────────────────────────────────
    const oneriIptal = async (symbol, mod) => {
        if (!window.confirm(`${symbol} önerisini iptal etmek istiyor musunuz?`)) return;
        try {
            const token = await getToken();
            await axios.post(`${API_URL}/scan/yom/iptal`, { symbol, mod },
                { headers: { Authorization: `Bearer ${token}` } });
            toast.success(`${symbol} önerisi iptal edildi`);
            setAktifOneriler(prev => prev.filter(o => o.symbol !== symbol));
            setDetayAcik(null);
        } catch (e) {
            toast.error("İptal edilemedi: " + (e.response?.data?.detail || e.message));
        }
    };

    // ── İşlemler: Toplu İptal ─────────────────────────────────────────────────
    const topluIptal = async () => {
        if (secilenler.size === 0) { toast.error("Önce sembol seçin"); return; }
        if (!window.confirm(`${secilenler.size} öneriyi iptal etmek istiyor musunuz?`)) return;
        setIslemMenuAcik(false);
        const token = await getToken();
        let basarili = 0;
        for (const symbol of secilenler) {
            const o = aktifOneriler.find(x => x.symbol === symbol) || (taramaSonucu?.oneriler || []).find(x => x.symbol === symbol);
            try {
                await axios.post(`${API_URL}/scan/yom/iptal`, { symbol, mod: o?.mod || piyasa },
                    { headers: { Authorization: `Bearer ${token}` } });
                basarili++;
            } catch { }
        }
        toast.success(`${basarili} öneri iptal edildi`);
        setSecilenler(new Set());
        aktifOnerileriYukle(piyasa);
    };

    // ── İşlemler: Alarm Ekle ──────────────────────────────────────────────────
    const alarmKaydet = async (hisseler, hedefFiyatlar, bildirim, mail) => {
        try {
            const { data: { session } } = await supabase.auth.getSession();
            for (const h of hisseler) {
                const giris = parseFloat(h.giris_fiyati);
                const hedef = parseFloat(hedefFiyatlar[h.symbol]);
                const pot = giris > 0 ? ((hedef - giris) / giris * 100).toFixed(2) : null;
                const { error } = await supabase.from("alerts").insert({
                    piyasa: h.mod || piyasa, sembol: h.symbol, para_birimi: h.para_birimi || paraBirimi,
                    fiyat: giris, hedef_fiyat: hedef, potansiyel: pot ? parseFloat(pot) : null,
                    bildirim_mail: mail, bildirim_popup: bildirim,
                    user_id: session.user.id,
                });
                if (error) throw error;
            }
            toast.success(`${hisseler.length} alarm eklendi!`);
            setAlarmPopup(false);
            setSecilenler(new Set());
        } catch (e) {
            toast.error("Alarm kaydedilemedi: " + e.message);
        }
    };

    const alarmAc = () => {
        if (secilenler.size === 0) { toast.error("Önce sembol seçin"); return; }
        setIslemMenuAcik(false);
        setAlarmPopup(true);
    };

    // ── Bildirim Tercih Kaydet ───────────────────────────────────────────────
    const bildirimKaydet = async () => {
        setBildirimKaydediliyor(true);
        try {
            const token = await getToken();
            await axios.post(`${API_URL}/scan/yom/bildirim-tercih`, bildirimTercih,
                { headers: { Authorization: `Bearer ${token}` } });
            toast.success("Bildirim tercihleri kaydedildi");
            setBildirimModal(false);
        } catch (e) {
            toast.error("Kaydedilemedi");
        } finally {
            setBildirimKaydediliyor(false);
        }
    };

    // ── Filtreleme & Sıralama ────────────────────────────────────────────────
    const siralamaToggle = (kol) =>
        setSiralama(s => ({ kol, yon: s.kol === kol && s.yon === "desc" ? "asc" : "desc" }));
    const siralamaOk = (kol) =>
        siralama.kol === kol ? (siralama.yon === "desc" ? " ↓" : " ↑") : "";

    const aktifOnerilerFiltreli = aktifOneriler
        .filter(o => filtreYon   === "Tümü" || o.yon === filtreYon)
        .filter(o => filtreTier  === "Tümü" || (o.guven_tier || "").includes(filtreTier))
        .filter(o => filtreVade  === "Tümü" || o.vade === filtreVade)
        .sort((a, b) => {
            const av = a[siralama.kol] ?? 0;
            const bv = b[siralama.kol] ?? 0;
            return siralama.yon === "desc" ? bv - av : av - bv;
        });

    const taramaOnerilerFiltreli = (taramaSonucu?.oneriler || [])
        .filter(o => filtreYon   === "Tümü" || o.yon === filtreYon)
        .filter(o => filtreTier  === "Tümü" || (o.guven_tier || "").includes(filtreTier))
        .filter(o => filtreVade  === "Tümü" || o.vade === filtreVade)
        .sort((a, b) => {
            const av = a[siralama.kol] ?? 0;
            const bv = b[siralama.kol] ?? 0;
            return siralama.yon === "desc" ? bv - av : av - bv;
        });

    const gosterilecekOneriler = tab === "aktif" ? aktifOnerilerFiltreli : taramaOnerilerFiltreli;

    const tumSecili = gosterilecekOneriler.length > 0 && secilenler.size === gosterilecekOneriler.length;
    const toggleTumSec = () => {
        if (tumSecili) setSecilenler(new Set());
        else setSecilenler(new Set(gosterilecekOneriler.map(o => o.symbol)));
    };
    const toggleSec = (symbol) => setSecilenler(prev => {
        const n = new Set(prev);
        n.has(symbol) ? n.delete(symbol) : n.add(symbol);
        return n;
    });

    // ── Tablo Başlığı ────────────────────────────────────────────────────────
    const thStyle = () => ({
        padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 700,
        color: C.muted, background: C.bg, borderBottom: `1px solid ${C.border}`,
        cursor: "pointer", whiteSpace: "nowrap", userSelect: "none",
    });

    const tdStyle = {
        padding: "10px 12px", fontSize: 12, borderBottom: `1px solid ${C.border}`,
        verticalAlign: "middle", color: C.text,
    };

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────

    return (
        <div style={{ minHeight: "100vh", width: "100%", background: C.bg }}>
        <div style={{ padding: "24px", fontFamily: "Inter, system-ui, sans-serif", color: C.text }}>

            {/* ── Başlık ── */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.text }}>
                        🎯 Yatırım Önerisi Motoru
                    </h1>
                    <p style={{ margin: "3px 0 0", fontSize: 13, color: C.muted }}>
                        Karar Motoru · ML Güven · Piyasa Rejimi · Risk Filtresi · Sentiment
                    </p>
                </div>
                <button onClick={() => setBildirimModal(true)}
                    style={{ marginLeft: "auto", padding: "7px 14px", borderRadius: 8,
                        border: `1px solid ${C.border}`, background: C.panel, color: C.text,
                        cursor: "pointer", fontSize: 13, fontWeight: 500 }}>
                    🔔 Bildirim Ayarları
                </button>
            </div>

            {/* ── Tarama Kriterleri (tarama sırasında pasif) ── */}
            <div style={{
                background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12,
                padding: "16px 20px", marginBottom: 18,
                display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap",
                opacity: calisiyor ? 0.5 : 1, pointerEvents: calisiyor ? "none" : "auto", transition: "opacity 0.3s",
            }}>
                {/* Piyasa */}
                <fieldset style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 12px 10px", margin: 0 }}>
                    <legend style={{ fontSize: 11, color: C.muted, fontWeight: 600, padding: "0 4px", letterSpacing: "0.05em" }}>PİYASA</legend>
                    <div style={{ display: "flex", gap: 6 }}>
                        {["BIST", "Kripto"].map(p => (
                            <button key={p} onClick={() => setPiyasa(p)} style={{
                                padding: "5px 14px", borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: "pointer",
                                border: `2px solid ${piyasa === p ? C.purple : C.border}`,
                                background: piyasa === p ? C.purple + "22" : "transparent",
                                color: piyasa === p ? C.purple : C.muted,
                            }}>
                                {p === "BIST" ? "🇹🇷 BIST" : "₿ Kripto"}
                                {piyasa === p && aktifOneriler.length > 0 && (
                                    <span style={{ marginLeft: 6, padding: "1px 6px", borderRadius: 20,
                                        background: C.purple + "44", fontSize: 11 }}>
                                        {aktifOneriler.length}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </fieldset>

                {/* Para Birimi */}
                <fieldset style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 12px 10px", margin: 0 }}>
                    <legend style={{ fontSize: 11, color: C.muted, fontWeight: 600, padding: "0 4px", letterSpacing: "0.05em" }}>PARA BİRİMİ</legend>
                    <div style={{ display: "flex", gap: 6 }}>
                        {paraBirimiSecenekleri.map(v => (
                            <button key={v} onClick={() => setParaBirimi(v)} style={{
                                padding: "5px 10px", borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: "pointer",
                                border: `2px solid ${paraBirimi === v ? "#a78bfa" : C.border}`,
                                background: paraBirimi === v ? "#a78bfa22" : "transparent",
                                color: paraBirimi === v ? "#a78bfa" : C.muted,
                            }}>{v}</button>
                        ))}
                    </div>
                </fieldset>

                {/* Vade */}
                <fieldset style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 12px 10px", margin: 0 }}>
                    <legend style={{ fontSize: 11, color: C.muted, fontWeight: 600, padding: "0 4px", letterSpacing: "0.05em" }}>VADE</legend>
                    <select value={vade} onChange={e => setVade(e.target.value)}
                        style={{ padding: "6px 10px", borderRadius: 6, border: `1px solid ${C.border}`,
                            fontSize: 13, background: C.bg, color: C.text, cursor: "pointer", outline: "none" }}>
                        {["Kısa Vade (1-10 gün)", "Orta Vade (11-60 gün)", "Uzun Vade (61+ gün)",
                          "Gün İçi", ...(piyasa === "Kripto" ? ["Kaldıraçlı İşlem"] : [])].map(v => (
                            <option key={v}>{v}</option>
                        ))}
                    </select>
                </fieldset>

                {/* Başlat Butonu */}
                <button onClick={taramaBaslat} disabled={calisiyor}
                    style={{
                        padding: "10px 24px", borderRadius: 8, border: "none",
                        background: calisiyor ? C.border : "linear-gradient(135deg,#4c1d95,#7c3aed)",
                        color: "white", fontWeight: 700, fontSize: 14,
                        cursor: calisiyor ? "not-allowed" : "pointer",
                    }}>
                    {calisiyor ? "⏳ Taranıyor..." : "🎯 Öneri Üret"}
                </button>

                {/* Yenile */}
                <button onClick={() => aktifOnerileriYukle(piyasa)}
                    style={{ padding: "9px 16px", borderRadius: 8, border: `1px solid ${C.border}`,
                        background: "transparent", color: C.muted, cursor: "pointer", fontSize: 13 }}>
                    🔄 Yenile
                </button>
            </div>

            {/* ── Tarama Uyarısı ── */}
            {calisiyor && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10,
                    padding: "10px 16px", borderRadius: 8, background: "#422006", border: "1px solid #92400e" }}>
                    <span style={{ fontSize: 16 }}>⚠️</span>
                    <span style={{ fontSize: 13, color: "#fde68a", fontWeight: 600 }}>
                        Tarama tamamlanıncaya kadar sayfayı kapatmayın
                    </span>
                </div>
            )}

            {/* ── İlerleme ── */}
            {calisiyor && (
                <YomIlerlemePaneli
                    ilerleme={ilerleme} mesaj={durumMesaj} bulunanSayisi={bulunanSayisi}
                    tamamlanan={tamamlanan} toplamSayisi={toplamSayisi}
                    gecenSure={gecenSure} tahminiSure={tahminiSure} mod={piyasa}
                />
            )}

            {/* ── Uyarı ── */}
            {taramaSonucu?.uyari && (
                <div style={{ background: C.gold + "18", borderRadius: 9, padding: "12px 16px",
                    color: C.gold, fontSize: 13, fontWeight: 600, marginBottom: 14,
                    border: `1px solid ${C.gold}44` }}>
                    {taramaSonucu.uyari}
                </div>
            )}

            {/* ── İçerik Tab + Filtreler ── */}
            <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "visible" }}>

                <div style={{ display: "flex", alignItems: "center", padding: "0 12px", borderBottom: `1px solid ${C.border}`, flexWrap: "wrap" }}>
                    {/* İşlemler menüsü */}
                    <div style={{ position: "relative", padding: "8px 8px 8px 0" }} ref={menuRef}>
                        <button onClick={() => setIslemMenuAcik(v => !v)} style={{
                            background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
                            padding: "7px 14px", display: "flex", alignItems: "center", gap: 6,
                            color: C.text, cursor: "pointer", fontSize: 12, fontWeight: 600,
                        }}>İşlemler ▾ {secilenler.size > 0 && `(${secilenler.size})`}</button>
                        {islemMenuAcik && (
                            <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, background: C.panel,
                                border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                                zIndex: 100, minWidth: 220, overflow: "visible" }}>
                                {[
                                    { label: "⏰ Alarm Ekle",           onClick: alarmAc },
                                    { label: "❌ Öneriyi İptal Et",     onClick: topluIptal },
                                ].map(item => (
                                    <button key={item.label} onClick={item.onClick}
                                        style={{ display: "block", width: "100%", textAlign: "left",
                                            padding: "10px 16px", border: "none", background: "none",
                                            fontSize: 13, borderBottom: `1px solid ${C.border}`,
                                            cursor: "pointer", color: C.text }}>{item.label}</button>
                                ))}
                                <IndirMenusu veri={gosterilecekOneriler} setIslemMenuAcik={setIslemMenuAcik} />
                            </div>
                        )}
                    </div>

                    {[
                        { key: "aktif",  label: `📌 Aktif Öneriler (${aktifOneriler.length})` },
                        { key: "tarama", label: `🎯 Son Tarama${taramaSonucu ? ` (${taramaSonucu.toplam})` : ""}` },
                    ].map(({ key, label }) => (
                        <button key={key} onClick={() => setTab(key)}
                            style={{
                                padding: "10px 16px", border: "none", background: "none",
                                cursor: "pointer", fontSize: 13, fontWeight: tab === key ? 700 : 400,
                                color: tab === key ? C.accent : C.muted,
                                borderBottom: tab === key ? `2px solid ${C.accent}` : "2px solid transparent",
                                marginBottom: -1,
                            }}>
                            {label}
                        </button>
                    ))}

                    {/* Filtreler */}
                    <div style={{ display: "flex", gap: 6, alignItems: "center", marginLeft: "auto", padding: "4px 0", flexWrap: "wrap" }}>
                        {/* Yön filtresi */}
                        {["Tümü", "AL", "SAT"].map(v => {
                            const renk = v === "AL" ? C.bullish : v === "SAT" ? C.bearish : C.accent;
                            return (
                                <button key={v} onClick={() => setFiltreYon(v)}
                                    style={{ padding: "3px 10px", borderRadius: 20, border: "none",
                                        cursor: "pointer", fontSize: 11, fontWeight: filtreYon === v ? 700 : 400,
                                        background: filtreYon === v ? renk + "22" : C.bg,
                                        color: filtreYon === v ? renk : C.muted }}>
                                    {v}
                                </button>
                            );
                        })}

                        <div style={{ width: 1, height: 16, background: C.border, margin: "0 2px" }} />

                        {/* Tier filtresi */}
                        {["Tümü", "Çok Yüksek", "Yüksek", "Orta"].map(v => (
                            <button key={v} onClick={() => setFiltreTier(v)}
                                style={{ padding: "3px 10px", borderRadius: 20, border: "none",
                                    cursor: "pointer", fontSize: 11, fontWeight: filtreTier === v ? 700 : 400,
                                    background: filtreTier === v ? C.accent + "33" : C.bg,
                                    color: filtreTier === v ? C.accent : C.muted }}>
                                {v}
                            </button>
                        ))}

                        <div style={{ width: 1, height: 16, background: C.border, margin: "0 2px" }} />

                        {/* Vade filtresi */}
                        {["Tümü", "Gün İçi", "Kısa Vade (1-10 gün)", "Orta Vade (11-60 gün)", "Uzun Vade (61+ gün)", ...(piyasa === "Kripto" ? ["Kaldıraçlı İşlem"] : [])].map(v => (
                            <button key={v} onClick={() => setFiltreVade(v)}
                                style={{ padding: "3px 10px", borderRadius: 20, border: "none",
                                    cursor: "pointer", fontSize: 11, fontWeight: filtreVade === v ? 700 : 400,
                                    background: filtreVade === v ? C.teal + "33" : C.bg,
                                    color: filtreVade === v ? C.teal : C.muted }}>
                                {v === "Tümü" ? "Tüm Vadeler" :
                                 v === "Gün İçi" ? "Gün İçi" :
                                 v === "Kısa Vade (1-10 gün)" ? "Kısa" :
                                 v === "Orta Vade (11-60 gün)" ? "Orta" :
                                 v === "Uzun Vade (61+ gün)" ? "Uzun" : v}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tablo */}
                {gosterilecekOneriler.length === 0 ? (
                    <div style={{ padding: "48px 24px", textAlign: "center", color: C.muted, fontSize: 14 }}>
                        {tab === "aktif"
                            ? "Henüz aktif öneri yok. 'Öneri Üret' butonuna basın."
                            : "Tarama yapılmadı veya eşik koşulları sağlanamadı."}
                    </div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr>
                                    <th style={{ ...thStyle(), width: 32, textAlign: "center" }}>
                                        <input type="checkbox" checked={tumSecili} onChange={toggleTumSec}
                                            style={{ cursor: "pointer" }} />
                                    </th>
                                    <th style={thStyle()} onClick={() => siralamaToggle("symbol")}>
                                        Sembol{siralamaOk("symbol")}
                                    </th>
                                    <th style={thStyle()}>Yön</th>
                                    <th style={thStyle()} onClick={() => siralamaToggle("toplam_skor")}>
                                        Skor{siralamaOk("toplam_skor")}<BilgiIkonu karti="skor" />
                                    </th>
                                    <th style={thStyle()}>Güven<BilgiIkonu karti="guven" /></th>
                                    <th style={thStyle()} onClick={() => siralamaToggle("giris_fiyati")}>
                                        Giriş{siralamaOk("giris_fiyati")}<BilgiIkonu karti="giris" />
                                    </th>
                                    <th style={thStyle()} onClick={() => siralamaToggle("hedef_fiyat")}>
                                        Hedef{siralamaOk("hedef_fiyat")}<BilgiIkonu karti="hedef" />
                                    </th>
                                    <th style={thStyle()} onClick={() => siralamaToggle("stop_loss")}>
                                        Stop{siralamaOk("stop_loss")}<BilgiIkonu karti="stop" />
                                    </th>
                                    <th style={thStyle()} onClick={() => siralamaToggle("rr")}>
                                        R:R{siralamaOk("rr")}<BilgiIkonu karti="rr" />
                                    </th>
                                    <th style={thStyle()} onClick={() => siralamaToggle("kazanc_pct")}>
                                        Kazanç%{siralamaOk("kazanc_pct")}<BilgiIkonu karti="kazanc_pct" />
                                    </th>
                                    <th style={thStyle()} onClick={() => siralamaToggle("kayip_pct")}>
                                        Kayıp%{siralamaOk("kayip_pct")}<BilgiIkonu karti="kayip_pct" />
                                    </th>
                                    <th style={thStyle()}>Pozisyon<BilgiIkonu karti="pozisyon" /></th>
                                    <th style={thStyle()}>Karar<BilgiIkonu karti="karar" /></th>
                                    <th style={thStyle()}>Tarih</th>
                                </tr>
                            </thead>
                            <tbody>
                                {gosterilecekOneriler.map(oneri => {
                                    const acik     = detayAcik === oneri.symbol;
                                    const yon      = yonRenk(oneri.yon);
                                    const durumu   = oneri.gecerli === false
                                        ? { bg: C.bearish + "11", border: C.bearish + "55" }
                                        : { bg: "transparent",    border: "transparent" };

                                    return [
                                        <tr key={oneri.symbol}
                                            style={{ background: durumu.bg,
                                                borderLeft: `3px solid ${acik ? C.purple : durumu.border}`,
                                                transition: "background 0.15s" }}>

                                            <td style={{ ...tdStyle, textAlign: "center" }} onClick={e => e.stopPropagation()}>
                                                <input type="checkbox" checked={secilenler.has(oneri.symbol)}
                                                    onChange={() => toggleSec(oneri.symbol)} style={{ cursor: "pointer" }} />
                                            </td>

                                            {/* Sembol */}
                                            <td style={{ ...tdStyle, fontWeight: 800, color: C.text, fontSize: 13, cursor: "pointer" }}
                                                onClick={() => setDetayAcik(acik ? null : oneri.symbol)}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                    <span>{acik ? "▼" : "▶"}</span>
                                                    {oneri.symbol}
                                                    {oneri.gecerli === false && (
                                                        <span style={{ fontSize: 10, color: C.bearish, fontWeight: 600 }}>
                                                            [İPTAL]
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Yön */}
                                            <td style={tdStyle}>
                                                <span style={{ padding: "3px 10px", borderRadius: 20,
                                                    fontSize: 11, fontWeight: 700,
                                                    background: yon.bg, color: yon.c }}>
                                                    {oneri.yon === "AL" ? "📈 AL" : "📉 SAT"}
                                                </span>
                                            </td>

                                            {/* Toplam Skor */}
                                            <td style={{ ...tdStyle, fontWeight: 800, color: skorRenk(oneri.toplam_skor || 0) }}>
                                                {Number(oneri.toplam_skor || 0).toFixed(1)}
                                            </td>

                                            {/* Güven Tier */}
                                            <td style={tdStyle}>
                                                <GuvenBadge tier={oneri.guven_tier} />
                                            </td>

                                            {/* Giriş */}
                                            <td style={{ ...tdStyle, fontWeight: 600 }}>
                                                {fmt(oneri.giris_fiyati, 4)}
                                            </td>

                                            {/* Hedef */}
                                            <td style={{ ...tdStyle, fontWeight: 700, color: C.bullish }}>
                                                {fmt(oneri.hedef_fiyat, 4)}
                                            </td>

                                            {/* Stop */}
                                            <td style={{ ...tdStyle, fontWeight: 700, color: C.bearish }}>
                                                {fmt(oneri.stop_loss, 4)}
                                            </td>

                                            {/* R:R */}
                                            <td style={{ ...tdStyle, fontWeight: 700, color: rrRenk(oneri.rr || 0) }}>
                                                {oneri.rr != null ? `${oneri.rr}:1` : "—"}
                                            </td>

                                            {/* Kazanç% */}
                                            <td style={{ ...tdStyle, fontWeight: 700, color: C.bullish }}>
                                                {fmtPct(oneri.kazanc_pct)}
                                            </td>

                                            {/* Kayıp% */}
                                            <td style={{ ...tdStyle, fontWeight: 700, color: C.bearish }}>
                                                {fmtPct(oneri.kayip_pct)}
                                            </td>

                                            {/* Pozisyon */}
                                            <td style={{ ...tdStyle, color: C.muted, fontSize: 11 }}>
                                                %{oneri.pozisyon_yuzdesi || "—"}
                                            </td>

                                            {/* Karar */}
                                            <td style={{ ...tdStyle, fontSize: 11 }}>
                                                {oneri.karar}
                                            </td>

                                            {/* Tarih */}
                                            <td style={{ ...tdStyle, fontSize: 11, color: C.muted }}>
                                                {oneri.olusturuldu_at
                                                    ? new Date(oneri.olusturuldu_at).toLocaleDateString("tr-TR", {
                                                        day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit"
                                                    })
                                                    : "—"}
                                            </td>
                                        </tr>,

                                        acik && (
                                            <DetayKart key={`${oneri.symbol}-detay`} oneri={oneri} onIptal={oneriIptal} />
                                        )
                                    ];
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Özet footer */}
                {gosterilecekOneriler.length > 0 && (
                    <div style={{ padding: "10px 16px", borderTop: `1px solid ${C.border}`,
                        display: "flex", gap: 16, alignItems: "center", fontSize: 12,
                        color: C.muted, background: C.bg }}>
                        <span>{gosterilecekOneriler.length} öneri gösteriliyor</span>
                        {[
                            { label: "🔥 Çok Yüksek", key: "Çok Yüksek" },
                            { label: "💚 Yüksek",     key: "Yüksek"     },
                            { label: "🔵 Orta",       key: "Orta"       },
                        ].map(({ label, key }) => {
                            const cnt = gosterilecekOneriler.filter(o => (o.guven_tier || "").includes(key)).length;
                            if (cnt === 0) return null;
                            const { c, bg } = tierRenk(key);
                            return (
                                <span key={key} style={{ padding: "2px 8px", borderRadius: 20,
                                    background: bg, color: c, fontSize: 11, fontWeight: 700 }}>
                                    {label}: {cnt}
                                </span>
                            );
                        })}
                        {tab === "tarama" && taramaSonucu?.tarih && (
                            <span style={{ marginLeft: "auto" }}>
                                Son tarama: {new Date(taramaSonucu.tarih).toLocaleString("tr-TR")}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* ── Alarm Popup ── */}
            {alarmPopup && (
                <AlarmPopup
                    hisseler={gosterilecekOneriler.filter(o => secilenler.has(o.symbol))}
                    onKapat={() => setAlarmPopup(false)}
                    onKaydet={alarmKaydet}
                />
            )}

            {/* ── Bildirim Tercihi Modal ── */}
            {bildirimModal && (
                <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
                    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                    <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, padding: 28,
                        width: 400, boxShadow: "0 8px 32px rgba(0,0,0,0.4)" }}>
                        <h3 style={{ margin: "0 0 18px", fontSize: 17, fontWeight: 800, color: C.text }}>
                            🔔 E-posta Bildirim Tercihleri
                        </h3>
                        <p style={{ margin: "0 0 16px", fontSize: 13, color: C.muted }}>
                            Hangi güven seviyelerinde e-posta bildirimi almak istiyorsunuz?
                        </p>
                        {[
                            { key: "eposta_cok_yuksek", label: "🔥 Çok Yüksek Güven (Skor ≥90)" },
                            { key: "eposta_yuksek",     label: "💚 Yüksek Güven (Skor 80-90)" },
                            { key: "eposta_orta",       label: "🔵 Orta Güven (Skor 70-80)" },
                            { key: "eposta_dusuk",      label: "🟡 Düşük Güven (Skor <70)" },
                        ].map(({ key, label }) => (
                            <label key={key} style={{ display: "flex", alignItems: "center", gap: 10,
                                padding: "10px 0", borderBottom: `1px solid ${C.border}`,
                                cursor: "pointer", fontSize: 13, color: C.text }}>
                                <input type="checkbox"
                                    checked={bildirimTercih[key] || false}
                                    onChange={e => setBildirimTercih(p => ({ ...p, [key]: e.target.checked }))}
                                    style={{ width: 16, height: 16, cursor: "pointer" }}
                                />
                                {label}
                            </label>
                        ))}
                        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                            <button onClick={() => setBildirimModal(false)}
                                style={{ flex: 1, padding: "9px 0", borderRadius: 9,
                                    border: `1px solid ${C.border}`, background: "transparent",
                                    cursor: "pointer", fontSize: 13, color: C.muted }}>
                                İptal
                            </button>
                            <button onClick={bildirimKaydet} disabled={bildirimKaydediliyor}
                                style={{ flex: 2, padding: "9px 0", borderRadius: 9,
                                    border: "none", background: bildirimKaydediliyor ? C.border : "linear-gradient(135deg,#1d4ed8,#3b82f6)",
                                    color: "white", fontWeight: 700, fontSize: 13,
                                    cursor: bildirimKaydediliyor ? "not-allowed" : "pointer" }}>
                                {bildirimKaydediliyor ? "Kaydediliyor..." : "💾 Kaydet"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
        </div>
    );
}
